import { useProfileStore } from "../../../../stores/UserStore";

// Couleurs adaptées au système de couleurs par défaut de Mantine
export const AVATARS = [
  {
    id: "nova",
    name: "Nova",
    src: "/avatars/agent-nova.png",
    gender: "female",
    accent: "violet",
  },
  {
    id: "leo",
    name: "Léo",
    src: "/avatars/agent-leo.png",
    gender: "male",
    accent: "cyan",
  },
  {
    id: "mia",
    name: "Mia",
    src: "/avatars/agent-mia.png",
    gender: "female",
    accent: "orange",
  },
  {
    id: "sam",
    name: "Sam",
    src: "/avatars/agent-sam.png",
    gender: "neutral",
    accent: "grape",
  },
];

export const LANGUAGES = [
  { code: "FRA", label: "Français", region: "France" },
  { code: "ENG", label: "Anglais", region: "International" },
  { code: "ESP", label: "Espagnol", region: "Espagne · LATAM" },
  { code: "DEU", label: "Allemand", region: "DACH" },
  { code: "ITA", label: "Italien", region: "Italie" },
];

export const TONES = [
  {
    id: "pro",
    label: "Professionnel & rassurant",
    description: "Cadre, crédible, orienté confiance.",
    prompt:
      "Adopte un ton professionnel, structuré et rassurant. Le vocabulaire doit être précis, inspirant confiance et crédibilité sans être trop rigide.",
  },
  {
    id: "warm",
    label: "Chaleureux & proche",
    description: "Humain, accessible, conversationnel.",
    prompt:
      "Adopte un ton chaleureux, humain et conversationnel. Utilise un langage accessible, empathique et crée rapidement une certaine proximité avec le prospect.",
  },
  {
    id: "direct",
    label: "Direct & orienté résultats",
    description: "Concis, percutant, orienté ROI.",
    prompt:
      "Adopte un ton direct, concis et orienté résultats. Va à l'essentiel, utilise des phrases courtes et mets en avant le ROI et l'efficacité de manière percutante.",
  },
  {
    id: "expert",
    label: "Consultatif & expert",
    description: "Pédagogue, apporte de la valeur.",
    prompt:
      "Adopte un ton consultatif, expert et pédagogue. Positionne-toi comme un conseiller apportant de la valeur ajoutée immédiate et une réflexion stratégique.",
  },
];

export const THINKING_STEPS = [
  {
    id: "site",
    label: "Analyse du site",
    detail: "Lecture des pages clés et du contenu",
  },
  {
    id: "activity",
    label: "Compréhension de l'activité",
    detail: "Secteur, positionnement, maturité",
  },
  {
    id: "offers",
    label: "Identification des offres",
    detail: "Produits, services et modèles",
  },
  {
    id: "personas",
    label: "Analyse des personas",
    detail: "Profils de décideurs et acheteurs",
  },
  {
    id: "targets",
    label: "Détection des cibles prioritaires",
    detail: "Segments à plus fort potentiel",
  },
  {
    id: "value",
    label: "Génération des propositions de valeur",
    detail: "Angles d'accroche différenciants",
  },
  {
    id: "persona",
    label: "Construction de la personnalité",
    detail: "Ton, posture et style de l'agent",
  },
];

export const EMPTY_DRAFT = {
  companyInput: "",
  companyName: "",
  companySummary: "",
  role: "",
  targets: [],
  valueProps: [],
  chosenValueProp: null,
  name: "",
  avatarId: null,
  gender: null,
  language: "Français",
  referentEmail: "",
  tone: "",
  instructions: "",
};

function prettifyName(input) {
  const trimmed = input.trim();
  const urlMatch = trimmed.match(/^(https?:\/\/)?(www\.)?([^/\s.]+)/i);
  if (urlMatch && /\.\w{2,}/.test(trimmed)) {
    const raw = urlMatch[3] || "votre entreprise";
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  const words = trimmed.split(/\s+/).slice(0, 3).join(" ");
  return words
    ? words.charAt(0).toUpperCase() + words.slice(1)
    : "Votre entreprise";
}

// Simulated "analysis". Produces a plausible, editable draft from the input.
export function analyzeCompany(input) {
  const companyName = prettifyName(input);
  return {
    companyName,
    companySummary: `${companyName} accompagne ses clients avec une offre à forte valeur ajoutée. L'entreprise se positionne sur un marché concurrentiel où la qualité de la relation commerciale fait la différence, avec un cycle de vente qui gagne à être nourri par une prospection régulière et personnalisée.`,
    role: "Business Developer",
    targets: [
      "Dirigeants de PME",
      "Responsables Marketing",
      "Décideurs Growth",
      "Directions Commerciales",
    ],
    valueProps: [
      `Générez un flux régulier de rendez-vous qualifiés sans alourdir l'équipe de ${companyName}.`,
      `Transformez l'expertise de ${companyName} en conversations commerciales concrètes et mesurables.`,
      `Ouvrez de nouveaux comptes stratégiques grâce à une approche personnalisée à grande échelle.`,
    ],
    name: "Nova",
    tone: "Professionnel & rassurant",
    instructions: `Tu es un agent commercial IA qui prospecte pour ${companyName}.\n\n• Adopte un ton professionnel et rassurant.\n• Personnalise chaque message selon le profil du prospect.\n• Mets en avant la proposition de valeur choisie sans être insistant.\n• Ton objectif est d'obtenir un rendez-vous qualifié, pas de vendre immédiatement.\n• Transmets les réponses des prospects au référent humain désigné.`,
  };
}

export function formatApiToDraft(apiResponse, input) {
  const companyName = prettifyName(input);

  // L'API renvoie un texte long pour le ton. On essaie de le faire correspondre
  // à l'un de vos badges "Recommandé" de l'UI (TONES). Sinon on met un par défaut.
  let matchedTone = "Professionnel & rassurant";
  const apiToneLower = apiResponse.tone.toLowerCase();

  if (apiToneLower.includes("direct") || apiToneLower.includes("concis")) {
    matchedTone = "Direct & orienté résultats";
  } else if (
    apiToneLower.includes("consultatif") ||
    apiToneLower.includes("expert")
  ) {
    matchedTone = "Consultatif & expert";
  } else if (
    apiToneLower.includes("chaleureux") ||
    apiToneLower.includes("proche")
  ) {
    matchedTone = "Chaleureux & proche";
  }

  // On formate les instructions supplémentaires de l'API sous forme de liste à puces
  const formattedInstructions = apiResponse.additional_instructions
    .split(". ")
    .filter((sentence) => sentence.trim().length > 0)
    .map((sentence) => sentence.trim())
    .join("\n");

  return {
    companyName,
    companySummary: apiResponse.activity_understanding,
    role: "Business Developer", // Valeur par défaut comme dans votre draft
    targets: apiResponse.targets,
    // targets: apiResponse.targets.slice(0, 4), // On en garde 4 pour l'esthétique de l'UI
    valueProps: apiResponse.value_propositions.slice(0, 3), // On en garde 3 (le choix idéal)
    name: useProfileStore.getState().profile?.first_name || "Nova",
    tone: matchedTone,
    instructions: formattedInstructions,
  };
}

export function computeProgress(draft) {
  const checks = [
    Boolean(draft.companyName),
    Boolean(draft.chosenValueProp),
    Boolean(draft.name),
    Boolean(draft.avatarId),
    Boolean(draft.gender),
    Boolean(draft.language),
    Boolean(draft.referentEmail),
    Boolean(draft.tone),
    Boolean(draft.instructions),
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

export function mapFrontToApiPayload(frontDraft) {
  // 1. Détecter l'URL du site web à partir de companyInput
  const urlRegex =
    /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,10}\b(?:[-a-zA-Z0-9@:%_+.~#?&//=]*)/i;
  const match = frontDraft.companyInput.match(urlRegex);
  let websiteUrl = frontDraft?.website_url || null;
  if (match && match[0]) {
    websiteUrl = /^https?:\/\//i.test(match[0])
      ? match[0]
      : `https://${match[0]}`;
  }

  // 2. Traduction de la langue vers le code ISO 3 lettres requis par l'API
  let isoLanguage = "fra"; // Par défaut
  if (
    frontDraft.language?.toLowerCase().includes("anglais") ||
    frontDraft.language?.toLowerCase().includes("en")
  ) {
    isoLanguage = "eng";
  } else if (
    frontDraft.language?.toLowerCase().includes("espagnol") ||
    frontDraft.language?.toLowerCase().includes("es")
  ) {
    isoLanguage = "spa";
  } else if (
    frontDraft.language?.toLowerCase().includes("allemand") ||
    frontDraft.language?.toLowerCase().includes("de")
  ) {
    isoLanguage = "deu";
  } else if (
    frontDraft.language?.toLowerCase().includes("italien") ||
    frontDraft.language?.toLowerCase().includes("it")
  ) {
    isoLanguage = "ita";
  } else if (
    frontDraft.language?.toLowerCase().includes("néerlandais") ||
    frontDraft.language?.toLowerCase().includes("nl")
  ) {
    isoLanguage = "nld";
  }

  // 3. Formatage des Cibles (targets) pour l'API
  const formattedTargets = frontDraft.targets.map((target, index) => ({
    value: target,
    sorting: index,
    active: true,
  }));

  // 4. Formatage des Propositions de Valeur (MULTI-SÉLECTION)
  // On transforme la chaîne (séparée par \n) en tableau
  const chosenPropsArray = frontDraft.chosenValueProp
    ? frontDraft.chosenValueProp.split("\n").map((p) => p.trim())
    : [];

  // On isole celles qui n'ont pas été choisies
  const unchosenPropsArray = frontDraft.valueProps.filter(
    (vp) => !chosenPropsArray.includes(vp),
  );

  // On place toutes les promesses choisies au début, suivies des autres
  const orderedValueProps = [...chosenPropsArray, ...unchosenPropsArray];

  const formattedValueProps = orderedValueProps.map((vp, index) => ({
    value: vp,
    sorting: index,
    // La proposition est active si elle fait partie des choix de l'utilisateur
    active: chosenPropsArray.includes(vp),
  }));

  // 6. Construction finale de l'objet API
  return {
    // --- CHAMPS ISSUS DIRECTEMENT DU FRONT ---
    name: frontDraft.name, // Remplacé "name: name" pour éviter l'erreur "name is not defined"
    description: ``,
    activity_description: frontDraft.companySummary,
    website_url: websiteUrl,
    brief: frontDraft.chosenValueProp || "", // Les promesses choisies avec \n
    gender: frontDraft.gender || "neutral",
    spoken_language: isoLanguage,
    bio: `IA ${frontDraft.role}`,
    tone: frontDraft.tone || "professional and concise",
    additional_instructions: frontDraft.instructions,

    avatar: frontDraft.avatarId || "nova",

    targets: formattedTargets,
    value_propositions: formattedValueProps,

    main_objective: frontDraft.main_objective,
    objective_link: frontDraft.objective_link,
    objective_link_type: frontDraft.objective_link_type,

    // Identifiants d'intégration
    integration_id: frontDraft.integration_id,
    linkedin_integration_id: frontDraft.linkedin_integration_id,
    email_signature_id: frontDraft.email_signature_id,

    // Infos Référent
    referent_name: frontDraft.referentEmail
      ? frontDraft.referentEmail.split("@")[0]
      : "Référent",
    referent_integration_id: frontDraft?.referentIntegrationId || null,
    rules: [],
    sharing: [],
    tags_ids: [],
    active: true,
  };
}

export const OBJECTIVES = [
  {
    id: "meeting_calendar",
    label: "Rediriger vers un lien de prise de rendez-vous",
    main_objective: "meeting",
    objective_link_type: "calendar",
    requiresInput: true,
    inputPlaceholder: "Ex: https://calendly.com/...",
  },
  {
    id: "registration",
    label: "Générer des inscriptions (plateforme, event, démo)",
    main_objective: "registration",
    objective_link_type: "link",
    requiresInput: true,
    inputPlaceholder: "Lien d'inscription (Ex: https://...)",
  },
  {
    id: "other",
    label: "Autre",
    main_objective: "other",
    objective_link_type: "other",
    requiresInput: true,
    inputPlaceholder: "Précisez votre objectif...",
  },
];
